import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE;

// --------------------------------------
// ✉️ Email Templates
// --------------------------------------
const TEMPLATES = {
  dispute: {
    subject: "⚠️ Job Dispute Update",
    message:
      "Hello,\n\nWe are currently reviewing your job dispute. Our support team will update you shortly.\n\n— Book Your Need Support",
  },
  cancellation: {
    subject: "❌ Job Cancelled",
    message:
      "Hello,\n\nYour job has been cancelled by our support team.\nIf you have any questions, please reply to this email.\n\n— Book Your Need Support",
  },
  verification: {
    subject: "✅ Verification Update",
    message:
      "Hello,\n\nYour verification status has been updated.\nPlease log in to your dashboard to view details.\n\n— Book Your Need Support",
  },
  general: {
    subject: "📢 Message from Book Your Need",
    message:
      "Hello,\n\nThis is a message from Book Your Need support.\n\n— BYN Team",
  },
};

const AdminCommunications = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  // =====================================================
  // 📡 Load all users
  // =====================================================
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/all-users`);
        setUsers(res.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load users");
      }
    };
    loadUsers();
  }, []);

  // =====================================================
  // 📜 Load email history
  // =====================================================
  const loadHistory = async (email) => {
    try {
      const res = await axios.get(
        `${API_BASE}/admin/email-history/${email}`
      );
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
  };

  // =====================================================
  // ✉️ Send Email
  // =====================================================
  const sendEmail = async () => {
    if (!selectedUser || !subject || !message) {
      return toast.error("Fill all fields");
    }

    try {
      setSending(true);
      await axios.post(`${API_BASE}/admin/send-email`, {
        to: selectedUser.email,
        subject,
        message,
        role: selectedUser.role,
      });

      toast.success("Email sent successfully ✅");
      setSubject("");
      setMessage("");
      loadHistory(selectedUser.email);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  // =====================================================
  // 🔍 Filter Users
  // =====================================================
  const filteredUsers = users.filter((u) => {
    if (filter !== "all" && u.role !== filter) return false;
    return (
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="flex font-[Poppins]">
      <Sidebar />

      <main className="ml-0 md:ml-64 w-full min-h-screen bg-gray-100 p-6">
        <h1 className="text-3xl font-extrabold mb-6 text-gray-800">
          📨 Communications Center
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ================= LEFT: USER LIST ================= */}
          <div className="bg-white rounded-2xl shadow p-4">
            <input
              type="text"
              placeholder="Search name or email..."
              className="w-full border rounded-lg px-3 py-2 mb-3"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="w-full border rounded-lg px-3 py-2 mb-4"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="customer">Customers</option>
              <option value="worker">Workers</option>
            </select>

            <div className="space-y-2 max-h-[65vh] overflow-y-auto">
              {filteredUsers.map((u) => (
                <div
                  key={u._id}
                  onClick={() => {
                    setSelectedUser(u);
                    setSubject("");
                    setMessage("");
                    loadHistory(u.email);
                  }}
                  className={`p-3 rounded-lg cursor-pointer border ${
                    selectedUser?.email === u.email
                      ? "bg-blue-50 border-blue-400"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="font-semibold">{u.name || "Unnamed"}</div>
                  <div className="text-sm text-gray-600">{u.email}</div>
                  <span className="text-xs text-indigo-600 capitalize">
                    {u.role}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ================= RIGHT: EMAIL PANEL ================= */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow p-6">
            {!selectedUser ? (
              <p className="text-gray-500">
                Select a user to start communication.
              </p>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-2">
                  ✉️ Email {selectedUser.name}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {selectedUser.email} • {selectedUser.role}
                </p>

                {/* Templates */}
                <select
                  className="w-full border rounded-lg px-3 py-2 mb-3"
                  onChange={(e) => {
                    const t = TEMPLATES[e.target.value];
                    if (t) {
                      setSubject(t.subject);
                      setMessage(t.message);
                    }
                  }}
                >
                  <option value="">Select Template</option>
                  {Object.keys(TEMPLATES).map((k) => (
                    <option key={k} value={k}>
                      {k.toUpperCase()}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Subject"
                  className="w-full border rounded-lg px-3 py-2 mb-3"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />

                <textarea
                  rows="6"
                  className="w-full border rounded-lg px-3 py-2 mb-4"
                  placeholder="Write your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />

                <button
                  onClick={sendEmail}
                  disabled={sending}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  {sending ? "Sending..." : "Send Email"}
                </button>

                {/* ================= EMAIL HISTORY ================= */}
                <div className="mt-6">
                  <h3 className="font-bold mb-2">📜 Email History</h3>
                  {history.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No previous emails.
                    </p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {history.map((h, i) => (
                        <li
                          key={i}
                          className="border rounded-lg p-3 bg-gray-50"
                        >
                          <div className="font-semibold">{h.subject}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(h.createdAt).toLocaleString()}
                          </div>
                          <div className="text-gray-700 mt-1">
                            {h.message}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCommunications;
